import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppConstants } from '../../../app.constants';
import { BrowserStorage } from '../../../utils/shared-services/browser-storage.service';
import { catchError, lastValueFrom, map, Observable, throwError } from 'rxjs';
import { HeadOfficePortalData } from '../models/head-office-portal.model';
import { UserAccess } from '../../users/models/user-access.model';
import { ReportData } from '../components/dialogs/ho-report-viewer-dialog.component';

@Injectable()
export class HOPortalApiService {
    public captions: any;
    private readonly BASE_URL: string;
    private readonly portalUrl: string = 'hoPortal';
    private readonly loginParam: string = '/login';
    private readonly hqLoginParam: string = '/hqlogin';
    private readonly validateParam: string = '/validate';
    private readonly configParam: string = '/config';
    private readonly dataParam: string = '/data';
    private readonly descriptionParam: string = '/description';
    private readonly HO_PORTAL_DATA_KEY: string = 'hoPortalData';
    private readonly gatwayOrder: string = "/gatewayOrder"

    constructor(
        @Inject(HttpClient) private http: HttpClient,
        @Inject(ActivatedRoute) private actRoute: ActivatedRoute,
        @Inject(BrowserStorage) private browserStorage: BrowserStorage
    ) {
        this.BASE_URL = AppConstants.BASE_URL;
        this.actRoute.data.subscribe((data) => {
            this.captions = data.captions;
        });
    }

    /**
 * @name loginForHOPortal
 * @description this method authenticates the user to access remote ho portal
 * @param storeID this is a unique string identifier for ho portal (a.k.a alias) [required]
 * @param user username [optional]
 * @param password password for login [required]
 * @returns access token and store information
 */
    async loginForHoPortal(alias, userID, password, userAccess) {
        let url = this.BASE_URL + this.portalUrl + this.loginParam;
        const data$ = this.http.post(
            url,
            JSON.stringify({
                storeSID: alias,
                user: userID ?? '',
                empID: password,
                userAccess: userAccess ? userAccess : {}, // for switching between stores
            })
        );
        const value = await lastValueFrom(data$);
        return value;
    }

    /**
     * @name hqLoginForHOPortal
     * @description this method authenticates the user to access remote ho portal. This method
     * requires a valid HQ token.
     * @param storeID this is a unique string identifier for ho portal (a.k.a alias) [required]
     * @param user username [optional]
     * @param password password for login [required]
     * @returns access token and store information
     */
    async hqLoginForHOPortal(storeID: string, userID: string, password: string, userAccess: UserAccess): Promise<HeadOfficePortalData> {
        let url = this.BASE_URL + this.portalUrl + this.hqLoginParam;
        const data$ = this.http.post<HeadOfficePortalData>(
            url,
            JSON.stringify({
                storeSID: storeID,
                user: userID ?? '',
                empID: password,
                userAccess: userAccess,
            })
        );
        const value = await lastValueFrom(data$);
        return value;
    }

    /**
* @name getWidgetDescriptionHTMLByID
* @description this method retrieves the widget description template HTML for a given widget by id.
* @param widgetID this is the id of the widget whose description template HTML needs to be retrieved [required]
* @returns description template HTML for a given widget
*/
    async getWidgetDescriptionHTMLByID(widgetID: string): Promise<string> {
        let url = this.BASE_URL + this.portalUrl + this.descriptionParam;
        const data$ = this.http.get(url, {
            params: {
                widgetID: widgetID,
            },
            responseType: 'text',
        });
        const value = await lastValueFrom(data$);
        return value;
    }

    /**
     * @name getDatasetsByDatapoints
     * @description this method retrieves the widget datasets for a given store and a given datapoints list. This
     * method requires a valid Store Portal token
     * @param string storeID: this is the id of the store whose widget datasets need to be retrieved [required]
     * @param string params: a stringified json object that includes all parameters used to filter data for 1 or more datapoints [required]
     * @param string datapoints: this is a list of datapoints for which the datasets need to be retrieved [required]
     * @returns datasets from store based on given list of datapoints
 
    */
    async getDatasetsByDatapoints(conceptID, params, datapoints) {
        let url = this.BASE_URL + this.portalUrl + this.dataParam;
        const data$ = this.http
            .get(url, {
                params: {
                    conceptID: conceptID,
                    params: params,
                    datapoints: datapoints,
                    token: this.browserStorage.getSessionstorage(this.HO_PORTAL_DATA_KEY, null).AccessToken,
                },
            })
            .pipe(catchError(this.handleError));
        const value = await lastValueFrom(data$);
        return value;
    }

    /**
 * @name viewCrystalReport
 * @description this method calls API to upload file to S3 and then open it in new tab
 * @param requestXML this is a unique string identifier for store portal (a.k.a alias) [required]
 * @returns url from S3
 */
    async viewCrystalReport(reportData: ReportData) {
        const data$ = this.http.post(this.BASE_URL + this.portalUrl + this.gatwayOrder, null, {
            params: {
                reportName: reportData.reportName,
                reportFile: reportData.source,
                date1: reportData.date1,
                date2: reportData.date2,
                detailLevel: reportData.detailLevel,
                storeID: reportData.storeID,
                conceptID: reportData.conceptID,
                storeNumList: reportData.storeNumList,
                storeBy: reportData.byStore,
                token: this.browserStorage.getSessionstorage(this.HO_PORTAL_DATA_KEY, null).AccessToken,
            }
        });
        const value = await lastValueFrom(data$);
        return value;
    }

    checkFileExists(fileUrl: string): Observable<boolean> {
        // Making a HEAD request to check if the file exists
        return this.http.head(fileUrl, { observe: 'response' }).pipe(
            map((response: HttpResponse<any>) => {
                // If the status is 200, the file exists
                return response.status === 200;
            })
        );
    }

    handleError(error: HttpErrorResponse) {
        return throwError(() => error);
    }
}